import type { BillableUsageExportValue } from "@coronet/cui";
import { useDialogsStore } from "@/_store/dialogs.module";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import {
  useMspBillableUsageStore,
  type BillableUsageExportDialogItem,
} from "@/_store/msp/msp-billable-usage.module.ts";
import MspExportBillingUsageModal from "@/components/modals/msp/MspExportBillingUsageModal.vue";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { showMspExportStartedToast } from "@/_helpers/msp-exports.ts";
import { MspExportAction } from "@/constants/msp-exports.ts";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";
import { ModalWidth } from "@/constants/modals";

export function useMspBillingUsageExport() {
  const dialogsStore = useDialogsStore();
  const permissionsStore = usePermissionsStore();
  const billableUsageStore = useMspBillableUsageStore();

  let isResolvingPeriod = false;

  const openBillingUsageExport = async ({
    workspaceId,
    period,
  }: {
    workspaceId: string;
    period?: string;
  }) => {
    let initialPeriod = period;
    if (!initialPeriod) {
      // Another click while the month is being resolved would open a second dialog.
      if (isResolvingPeriod) return;
      isResolvingPeriod = true;
      initialPeriod = await billableUsageStore.resolveDefaultPeriod(workspaceId);
      isResolvingPeriod = false;
    }

    dialogsStore.openDialog(
      componentDialogsConfigConstructor<BillableUsageExportDialogItem>({
        item: {
          workspaceId,
          period: initialPeriod,
          periods: billableUsageStore.periods,
        },
        action: MspExportAction.EXPORT_BILLING_USAGE,
        component: MspExportBillingUsageModal,
        width: ModalWidth.LARGE,
        disable: isMspActionRestricted(
          MspRolePermissionScopes.MSP_PORTAL_SCOPE,
          MspPortalScopeSection.MSP_REPORTS
        ),
        callback: async (value: BillableUsageExportValue) => {
          if (await billableUsageStore.exportBillingUsage({ ...value, workspaceId })) {
            // Widget and Exports menu alike: View opens the Exports tab (PLAT-4536 R-16/R-17).
            showMspExportStartedToast(permissionsStore.isGlobalAdmin);
          }
        },
      })
    );
  };

  return { openBillingUsageExport };
}
